import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Eye, HeartHandshake, Route } from "lucide-react";
import { scrollToId, site } from "../config/site";

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "112%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const values = [
  {
    icon: Eye,
    title: "Comprender antes de intervenir",
    desc: "Cada intervención nace de entender cómo aprende cada persona.",
  },
  {
    icon: HeartHandshake,
    title: "Acompañar desde la confianza",
    desc: "Un espacio cercano donde niños, niñas, adolescentes y familias se sienten escuchados.",
  },
  {
    icon: Route,
    title: "Aprender con estrategias adaptadas",
    desc: "Herramientas diseñadas a la medida de cada niño/a y cada familia.",
  },
];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 26]);
  const bgY = useTransform(scrollYProgress, [0, 1], [0, 60]);

  return (
    <section
      id="inicio"
      ref={ref}
      data-testid="hero-section"
      className="relative overflow-hidden pt-[76px]"
    >
      {/* soft background shapes */}
      <motion.div
        style={{ y: bgY }}
        aria-hidden="true"
        className="absolute -top-32 -right-40 w-[560px] h-[560px] rounded-full bg-sage-light/70 blur-3xl"
      />
      <div
        aria-hidden="true"
        className="absolute top-1/2 -left-48 w-[420px] h-[420px] rounded-full bg-wood-light/80 blur-3xl"
      />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 sm:pt-16 lg:pt-24 pb-16 lg:pb-24">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          <div className="lg:col-span-7">
            <motion.p
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.15 }}
              className="eyebrow flex items-center gap-3"
              data-testid="hero-eyebrow"
            >
              <span className="inline-block w-8 h-px bg-sage-dark" />
              {site.descriptor} · Chiclana de la Frontera
            </motion.p>

            <h1
              data-testid="hero-title"
              className="mt-6 font-serif font-semibold text-atento-blue tracking-tight leading-[1.06] text-[2.6rem] sm:text-6xl lg:text-[4.4rem]"
            >
              <MaskedLine delay={0.25}>Cada persona tiene</MaskedLine>
              <MaskedLine delay={0.4}>
                <em className="not-italic font-serif italic text-sage-dark">
                  su propio ritmo
                </em>
              </MaskedLine>
              <MaskedLine delay={0.55}>de aprendizaje.</MaskedLine>
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.85 }}
              data-testid="hero-subtitle"
              className="mt-7 max-w-xl text-base sm:text-lg leading-relaxed text-charcoal/70"
            >
              En ATENTO acompañamos a niños, niñas, adolescentes y familias para
              comprender sus necesidades, desarrollar sus capacidades y avanzar
              con confianza.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1 }}
              className="mt-9 flex flex-col sm:flex-row gap-4"
            >
              <button
                onClick={() => scrollToId("contacto")}
                data-testid="hero-cta-primary"
                className="inline-flex items-center justify-center gap-2 bg-atento-blue hover:bg-atento-dark text-white font-accent font-semibold px-8 py-4 rounded-full shadow-md hover:shadow-lg transition-all duration-200 hover:-translate-y-0.5 cursor-pointer"
              >
                Cuéntame qué necesitas
                <ArrowRight className="w-4 h-4" />
              </button>
              <button
                onClick={() => scrollToId("servicios")}
                data-testid="hero-cta-secondary"
                className="inline-flex items-center justify-center gap-2 bg-sage/10 hover:bg-sage/20 text-atento-blue border border-sage/30 font-accent font-semibold px-8 py-4 rounded-full transition-all duration-200 cursor-pointer"
              >
                Descubre nuestros servicios
              </button>
            </motion.div>
          </div>

          <div className="lg:col-span-5 relative">
            <motion.div
              initial={{ opacity: 0, scale: 0.96, y: 24 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ duration: 1.1, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
              className="relative"
            >
              <div
                aria-hidden="true"
                className="absolute -top-5 -left-5 w-full h-full rounded-t-[10rem] rounded-b-[2rem] bg-wood/40"
              />
              <div className="relative overflow-hidden rounded-t-[10rem] rounded-b-[2rem] shadow-[0_24px_60px_rgba(27,59,111,0.16)] aspect-[4/5]">
                <motion.img
                  style={{ y: imgY, scale: 1.1, objectPosition: "50% 12%" }}
                  src="/uploads/lidia-hero.webp"
                  alt="Lidia Montiel, pedagoga y orientadora educativa, en el gabinete ATENTO de Chiclana de la Frontera"
                  data-testid="hero-image"
                  className="w-full h-full object-cover"
                  loading="eager"
                />
              </div>
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 1.15 }}
                className="absolute -bottom-6 -left-4 sm:-left-8 bg-white/80 backdrop-blur-xl border border-atento-blue/10 rounded-2xl px-6 py-4 shadow-lg text-center"
                data-testid="hero-slogan-card"
              >
                <p className="text-xs font-accent font-medium uppercase tracking-[0.16em] text-sage-dark">
                  Pedagoga y orientadora educativa
                </p>
              </motion.div>
            </motion.div>
          </div>
        </div>

        <div className="mt-20 lg:mt-28 grid sm:grid-cols-3 gap-px bg-atento-blue/10 rounded-2xl overflow-hidden border border-atento-blue/10">
          {values.map((v, i) => (
            <motion.div
              key={v.title}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: i * 0.12 }}
              data-testid={`hero-value-${i}`}
              className="bg-ivory p-7 sm:p-8 group hover:bg-white transition-colors duration-300"
            >
              <v.icon
                className="w-6 h-6 text-sage-dark group-hover:text-atento-blue transition-colors duration-300"
                strokeWidth={1.8}
              />
              <h3 className="mt-4 font-accent font-semibold text-atento-blue text-base sm:text-lg leading-snug">
                {v.title}
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-charcoal/60">{v.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
